import { useEffect } from 'react';
import './AcrobaticsAnimation.css';

/**
 * PROFESSIONAL ACROBATICS ANIMATION
 * Flying-type physical move with aerial acrobatic maneuvers
 * 
 * Key Features:
 * - Flying-type physical attack (55 base power, 100% accuracy)
 * - Doubles to 110 power if user has no held item
 * - Aerial acrobatic maneuvers with agile movements
 * - Professional gaming company quality with aerial timing
 * - Physical move animation with flight and contact mechanics
 */
export default function AcrobaticsAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate flight trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Aerial Preparation (0-400ms)
    console.log('Acrobatics Phase 1: Aerial Preparation');
    
    // Player enhancement with aerial aura
    playerEl.classList.add('acrobatics-user-charge');
    
    // Aerial wind aura around player
    const aerialAura = document.createElement('div');
    aerialAura.className = 'acrobatics-aerial-aura';
    aerialAura.style.left = `${playerX - 90}px`;
    aerialAura.style.top = `${playerY - 90}px`;
    container.appendChild(aerialAura);
    elements.push(aerialAura);

    // Wind current particles around player
    for (let i = 0; i < 16; i++) {
      const windParticle = document.createElement('div');
      windParticle.className = 'acrobatics-wind-particle';
      const angle = (i / 16) * 360;
      const radius = 60 + (i % 4) * 12;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      windParticle.style.left = `${particleX - 4}px`;
      windParticle.style.top = `${particleY - 4}px`;
      windParticle.style.animationDelay = `${i * 20}ms`;
      windParticle.textContent = '💨';
      container.appendChild(windParticle);
      elements.push(windParticle);
    }

    // Feather effects showing aerial nature
    for (let i = 0; i < 8; i++) {
      const feather = document.createElement('div');
      feather.className = 'acrobatics-feather-effect';
      const featherAngle = (i / 8) * 360;
      const featherRadius = 75;
      const featherX = playerX + Math.cos(featherAngle * Math.PI / 180) * featherRadius;
      const featherY = playerY + Math.sin(featherAngle * Math.PI / 180) * featherRadius;
      feather.style.left = `${featherX - 6}px`;
      feather.style.top = `${featherY - 6}px`;
      feather.style.animationDelay = `${i * 40}ms`;
      feather.textContent = '🪶';
      container.appendChild(feather);
      elements.push(feather);
    }

    // PHASE 2: Aerial Acrobatic Flight (400-900ms)
    const phase2Timer = setTimeout(() => {
      console.log('Acrobatics Phase 2: Aerial Acrobatic Flight');
      
      // Player aerial flight with multiple loops
      playerEl.classList.add('acrobatics-user-flight');
      playerEl.style.setProperty('--flight-x', `${dx * 0.9}px`);
      playerEl.style.setProperty('--flight-y', `${dy * 0.9}px`);

      // Aerial trail during acrobatic flight
      for (let i = 0; i < 10; i++) {
        const trailTimer = setTimeout(() => {
          const aerialTrail = document.createElement('div');
          aerialTrail.className = 'acrobatics-aerial-trail';
          const progress = i / 10;
          // Create looping trajectory for acrobatic effect
          const loopX = playerX + (dx * 0.9 * progress) + Math.sin(progress * Math.PI * 3) * 30;
          const loopY = playerY + (dy * 0.9 * progress) - Math.abs(Math.sin(progress * Math.PI * 2)) * 40;
          aerialTrail.style.left = `${loopX - 4}px`;
          aerialTrail.style.top = `${loopY - 4}px`;
          aerialTrail.textContent = '✨';
          container.appendChild(aerialTrail);
          elements.push(aerialTrail);
        }, i * 35);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Wind currents during flight
      for (let i = 0; i < 12; i++) {
        const currentTimer = setTimeout(() => {
          const windCurrent = document.createElement('div');
          windCurrent.className = 'acrobatics-wind-current';
          const progress = i / 12;
          const currentX = playerX + (dx * 0.9 * progress) + (Math.random() - 0.5) * 50;
          const currentY = playerY + (dy * 0.9 * progress) + (Math.random() - 0.5) * 30;
          windCurrent.style.left = `${currentX - 8}px`;
          windCurrent.style.top = `${currentY - 8}px`;
          container.appendChild(windCurrent);
          elements.push(windCurrent);
        }, i * 30);
        elements.push({ timer: currentTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Acrobatic Strike Impact (900-1400ms)
    const phase3Timer = setTimeout(() => {
      console.log('Acrobatics Phase 3: Acrobatic Strike Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('acrobatics-opponent-hit');

      // Reset player position with aerial strike
      playerEl.classList.remove('acrobatics-user-flight');
      playerEl.classList.add('acrobatics-user-strike');

      // Massive aerial impact explosion
      const aerialImpact = document.createElement('div');
      aerialImpact.className = 'acrobatics-aerial-impact';
      aerialImpact.style.left = `${opponentX - 110}px`;
      aerialImpact.style.top = `${opponentY - 110}px`;
      container.appendChild(aerialImpact);
      elements.push(aerialImpact);

      // Wind burst particles
      for (let i = 0; i < 20; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'acrobatics-wind-burst';
        const burstAngle = (i / 20) * 360;
        const burstRadius = 70 + Math.random() * 50;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${burstX - 4}px`;
        burstParticle.style.top = `${burstY - 4}px`;
        burstParticle.style.animationDelay = `${i * 15}ms`;
        burstParticle.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * 35}px`);
        burstParticle.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * 35}px`);
        burstParticle.textContent = '💨';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }

      // Aerial shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'acrobatics-shockwave';
          shockwave.style.left = `${opponentX - 75}px`;
          shockwave.style.top = `${opponentY - 75}px`;
          shockwave.style.animationDelay = `${i * 80}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 100);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Feather burst effects
      for (let i = 0; i < 12; i++) {
        const featherBurst = document.createElement('div');
        featherBurst.className = 'acrobatics-feather-burst';
        const featherAngle = (i / 12) * 360;
        const featherRadius = 85;
        const featherX = opponentX + Math.cos(featherAngle * Math.PI / 180) * featherRadius;
        const featherY = opponentY + Math.sin(featherAngle * Math.PI / 180) * featherRadius;
        featherBurst.style.left = `${featherX - 5}px`;
        featherBurst.style.top = `${featherY - 5}px`;
        featherBurst.style.animationDelay = `${i * 30}ms`;
        featherBurst.textContent = '🪶';
        container.appendChild(featherBurst);
        elements.push(featherBurst);
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Aerial Recovery (1400-1700ms)
    const phase4Timer = setTimeout(() => {
      console.log('Acrobatics Phase 4: Aerial Recovery');
      
      // Remove strike effects
      playerEl.classList.remove('acrobatics-user-charge', 'acrobatics-user-strike');
      
      // Aerial recovery wind effects
      const recoveryWind = document.createElement('div');
      recoveryWind.className = 'acrobatics-recovery-wind';
      recoveryWind.style.left = `${playerX - 80}px`;
      recoveryWind.style.top = `${playerY - 80}px`;
      container.appendChild(recoveryWind);
      elements.push(recoveryWind);

      // Gentle feathers floating down
      for (let i = 0; i < 6; i++) {
        const floatingFeather = document.createElement('div');
        floatingFeather.className = 'acrobatics-floating-feather';
        const featherX = opponentX + (Math.random() - 0.5) * 120;
        const featherY = opponentY - 60 + (Math.random() - 0.5) * 40;
        floatingFeather.style.left = `${featherX - 4}px`;
        floatingFeather.style.top = `${featherY - 4}px`;
        floatingFeather.style.animationDelay = `${i * 50}ms`;
        floatingFeather.textContent = '🪶';
        container.appendChild(floatingFeather);
        elements.push(floatingFeather);
      }
    }, 1400);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Acrobatics: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('acrobatics-user-charge', 'acrobatics-user-flight', 'acrobatics-user-strike');
      opponentEl.classList.remove('acrobatics-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1700);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('acrobatics-user-charge', 'acrobatics-user-flight', 'acrobatics-user-strike');
      opponentEl.classList.remove('acrobatics-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
AcrobaticsAnimation.moveType = 'flying';
